// Delete-confirmation modal for employees (ADR-027 v9). Shared by the single
// delete (employee detail page) and the bulk delete (list multi-select). Mirrors
// the ChangePasswordDialog shell; the confirm button is danger-red.
//
// Deletion is a soft-delete + PDPA face-template erasure on the backend;
// historical InfluxDB measurements are kept — the note spells that out so the
// admin isn't surprised.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { useI18n } from "@faceheart/i18n";
import { employeesApi } from "../../lib/api/endpoints";
import { ApiError } from "../../lib/api/client";

export type DeleteTarget = { id: string; name: string };

export function DeleteEmployeeDialog({
  targets,
  onClose,
  onDeleted,
}: {
  targets: DeleteTarget[];
  onClose: () => void;
  // Called after a successful delete (e.g. navigate away / clear selection).
  onDeleted?: () => void;
}) {
  const { t } = useI18n("admin");
  const qc = useQueryClient();
  const isBulk = targets.length > 1;

  const mut = useMutation({
    mutationFn: async () => {
      const ids = targets.map((x) => x.id);
      if (ids.length > 1) {
        return employeesApi.bulkDelete(ids);
      }
      await employeesApi.remove(ids[0]!);
      return { deleted: 1 };
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ["employees"] });
      toast.success(
        isBulk
          ? t("employees.delete.successMany", { count: res.deleted })
          : t("employees.delete.success"),
      );
      onDeleted?.();
      onClose();
    },
    onError: (err) =>
      toast.error(
        t("employees.delete.failed", {
          code: err instanceof ApiError ? err.code : "internal",
        }),
      ),
  });

  const message = isBulk
    ? t("employees.delete.confirmMany", { count: targets.length })
    : t("employees.delete.confirmOne", { name: targets[0]?.name || "—" });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("employees.delete.title")}
        className="w-96 max-w-[92vw] rounded-lg border border-admin-border bg-admin-bg2 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-medium flex items-center gap-2 text-admin-red">
            <Trash2 className="w-4 h-4" /> {t("employees.delete.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-admin-text3 hover:text-admin-text1"
            aria-label={t("common:actions.close")}
          >
            ✕
          </button>
        </div>

        <p className="text-sm text-admin-text1">{message}</p>
        <p className="mt-2 text-xs text-admin-text3">{t("employees.delete.note")}</p>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-admin-border px-4 py-2 text-sm text-admin-text2 hover:text-admin-text1"
          >
            {t("common:actions.cancel")}
          </button>
          <button
            type="button"
            disabled={mut.isPending || targets.length === 0}
            onClick={() => mut.mutate()}
            className="rounded bg-admin-red/20 border border-admin-red/40 text-admin-red px-4 py-2 text-sm hover:bg-admin-red/30 disabled:opacity-50"
          >
            {mut.isPending
              ? t("employees.delete.submitting")
              : t("employees.delete.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
